import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Radio, { RadioGroup } from '@8x8/oxygen-radio'
import Checkbox, { CheckboxGroup } from '@8x8/oxygen-checkbox'
import Tag, { variants } from '@8x8/oxygen-tag'
import Select from '@8x8/oxygen-select'
import Tooltip from '@8x8/oxygen-tooltip'

import { getModerationSettings, getUser, isLargePreset, getOverridePermissions } from '~/selectors/room-settings'
import { getContacts, updateModerationSettings } from '~/actions/room-settings'
import { LARGE_LAYOUT_THRESHOLD, MODERATION_TYPE, ROLE, VISITOR_PROMOTION_TYPE } from '~/core/constants'
import { translate } from '~/core/translate'
import { AddParticipants, Participants, SettingsContainer, SettingsDescription, SettingsTitle, StyledLegend } from './components'

const Settings = styled.div`
  display: flex;
  flex-direction: ${props => props.useColumn ? 'column' : 'row'};
  gap: ${props => props.useColumn ? '16px' : '0'};

  @media (max-width: ${LARGE_LAYOUT_THRESHOLD}px) {
    flex-direction: column-reverse;
    gap: 20px;
  }
`

const ModerationSetting = styled.div`
  flex-grow: 1;
`

const ModerationSettings = () => {
  const dispatch = useDispatch()
  const [fetching, setFetching] = useState(false)
  const [filterValue, setFilterValue] = useState('')
  const [items, setItems] = useState([])
  const user = useSelector(getUser)
  const isLargeMeeting = useSelector(isLargePreset)
  const overridePermissions = useSelector(getOverridePermissions)
  const {
    moderationType,
    joinMuted,
    joinHidden,
    abilityToUnmute,
    visitorPromotionType,
    activeParticipantUsers
  } = useSelector(getModerationSettings)

  const isModerationTypeDisabled = useMemo(
    () => !overridePermissions.moderationType,
    [overridePermissions.moderationType]
  )

  const handleSelectChange = useCallback(value => {
    setFilterValue(value)
  }, [])

  const fetchContacts = useCallback(async query => {
    setFetching(true)
    try {
      const response = await dispatch(getContacts({ query }))
      const formatted = response
        .map(contact => ({
          value: contact.user_id,
          label: contact.name
        }))
        .filter(({ value }) => !!value && value !== user.id)

      setItems(formatted)
    } finally {
      setFetching(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchContacts(filterValue)
  }, [filterValue, fetchContacts])

  const handleModeratorsUpdate = useCallback(payload => {
    let users = []

    if (payload === MODERATION_TYPE.SPECIFIC_MEMBERS) {
      users = [{ userId: user.id, name: user.name, activeParticipantType: ROLE.MODERATOR }]
    }

    dispatch(updateModerationSettings({
      moderationType: payload,
      activeParticipantUsers: users
    }))
  }, [dispatch, user])

  const handleJoinUpdate = useCallback(payload => {
    let enhancedPayload = { ...payload }

    if (payload.abilityToUnmute === false) {
      enhancedPayload = {
        ...enhancedPayload,
        joinMuted: true,
        joinHidden: true
      }
    }

    if (payload.abilityToUnmute === true) {
      if (typeof joinMuted === 'undefined') {
        enhancedPayload.joinMuted = false
      }
      if (typeof joinHidden === 'undefined') {
        enhancedPayload.joinHidden = false
      }
    }

    if (payload.joinMuted === false || payload.joinHidden === false) {
      enhancedPayload.abilityToUnmute = true
    }

    dispatch(updateModerationSettings(enhancedPayload))
  }, [dispatch])

  const handleModerationUsersUpdate = useCallback(user => {
    if (!user) {
      return
    }

    const { value, label } = user
    if (activeParticipantUsers.find(({ userId }) => userId === value)) {
      return
    }

    dispatch(updateModerationSettings({
      moderationType: MODERATION_TYPE.SPECIFIC_MEMBERS,
      activeParticipantUsers: [
        ...activeParticipantUsers,
        { userId: value, name: label, activeParticipantType: ROLE.MODERATOR }
      ]
    }))
  }, [dispatch, activeParticipantUsers])

  const handleRemoveModerator = useCallback(userId => {
    dispatch(updateModerationSettings({
      moderationType: MODERATION_TYPE.SPECIFIC_MEMBERS,
      activeParticipantUsers: activeParticipantUsers.filter(({ userId: id }) => id !== userId)
    }))
  }, [dispatch, activeParticipantUsers])

  const handleVisitorPromotionUpdate = useCallback(payload => {
    dispatch(updateModerationSettings({
      visitorPromotionType: payload
    }))
  })

  if (isLargeMeeting) {
    return (
      <SettingsContainer>
        <SettingsTitle>{translate('promotion')}</SettingsTitle>
        <Settings useColumn>
          <ModerationSetting>
            <SettingsDescription>{translate('visitor_to_participant_description')}</SettingsDescription>
            <RadioGroup onChange={handleVisitorPromotionUpdate} value={visitorPromotionType}>
              <StyledLegend>{translate('visitor_to_participant')}</StyledLegend>
              <Radio
                label={<span>{translate('visitor_to_participant_only_mods')}</span>}
                value={VISITOR_PROMOTION_TYPE.MODERATOR_APPROVAL}
                name='MODERATOR_APPROVAL'
              />
              <Radio
                label={<span>{translate('visitor_to_participant_auto')}</span>}
                value={VISITOR_PROMOTION_TYPE.RAISE_HAND}
                name='RAISE_HAND'
              />
            </RadioGroup>
          </ModerationSetting>
        </Settings>
      </SettingsContainer>
    )
  }

  return (
    <SettingsContainer noBorder>
      <SettingsTitle>{translate('moderation')}</SettingsTitle>
      <Settings>
        <ModerationSetting>
          {isModerationTypeDisabled
            ? (
              <Tooltip
                title={translate('admin_controlled_setting')}
                orientation='top'
                showOn='hover'
              >
                <RadioGroup onChange={handleModeratorsUpdate} value={moderationType}>
                  <StyledLegend>{translate('who_can_moderate')}</StyledLegend>
                  <Radio
                    label={<span>{translate('org_members')}</span>}
                    value={MODERATION_TYPE.ALL_MEMBERS}
                    name='ALL_MEMBERS'
                    isDisabled={isModerationTypeDisabled}
                  />
                  <Radio
                    label={<span>{translate('only_me')}</span>}
                    value={MODERATION_TYPE.ONLY_ME}
                    name='ONLY_ME'
                    isDisabled={isModerationTypeDisabled}
                  />
                  <Radio
                    label={<span>{translate('specific_members')}</span>}
                    value={MODERATION_TYPE.SPECIFIC_MEMBERS}
                    name='SPECIFIC_MEMBERS'
                    isDisabled={isModerationTypeDisabled}
                  />
                </RadioGroup>
              </Tooltip>
              )
            : (
              <RadioGroup onChange={handleModeratorsUpdate} value={moderationType}>
                <StyledLegend>{translate('who_can_moderate')}</StyledLegend>
                <Radio
                  label={<span>{translate('org_members')}</span>}
                  value={MODERATION_TYPE.ALL_MEMBERS}
                  name='ALL_MEMBERS'
                />
                <Radio
                  label={<span>{translate('only_me')}</span>}
                  value={MODERATION_TYPE.ONLY_ME}
                  name='ONLY_ME'
                />
                <Radio
                  label={<span>{translate('specific_members')}</span>}
                  value={MODERATION_TYPE.SPECIFIC_MEMBERS}
                  name='SPECIFIC_MEMBERS'
                />
              </RadioGroup>
              )}
        </ModerationSetting>
        <ModerationSetting>
          <CheckboxGroup>
            <StyledLegend>{translate('participant_options')}</StyledLegend>
            <Checkbox
              label={translate('join_muted')}
              isChecked={joinMuted}
              onChange={() => handleJoinUpdate({ joinMuted: !joinMuted })}
            />
            <Checkbox
              label={translate('join_hidden')}
              isChecked={joinHidden}
              onChange={() => handleJoinUpdate({ joinHidden: !joinHidden })}
            />
            <Checkbox
              label={translate('ability_to_unmute')}
              isChecked={abilityToUnmute}
              onChange={() => handleJoinUpdate({ abilityToUnmute: !abilityToUnmute })}
            />
          </CheckboxGroup>
        </ModerationSetting>
      </Settings>

      {moderationType === MODERATION_TYPE.SPECIFIC_MEMBERS && (
        <AddParticipants>
          <StyledLegend>{translate('add_moderators')}</StyledLegend>
          {isModerationTypeDisabled
            ? (
              <Tooltip
                title={translate('admin_controlled_setting')}
                orientation='top'
                showOn='hover'
              >
                <Select
                  placeholder={translate('search_for_contacts')}
                  options={items}
                  onInputChange={handleSelectChange}
                  value={filterValue}
                  components={{
                    DropdownIndicator: undefined
                  }}
                  isSearchable
                  menuIsOpen={Boolean(filterValue)}
                  noOptionsMessage={() => translate('no_results_found')}
                  onChange={handleModerationUsersUpdate}
                  isLoading={fetching}
                  isDisabled={isModerationTypeDisabled}
                  testId='moderator-selection'
                />
              </Tooltip>
              )
            : (
              <Select
                placeholder={translate('search_for_contacts')}
                options={items}
                onInputChange={handleSelectChange}
                value={filterValue}
                components={{
                  DropdownIndicator: undefined
                }}
                isSearchable
                menuIsOpen={Boolean(filterValue)}
                noOptionsMessage={() => translate('no_results_found')}
                onChange={handleModerationUsersUpdate}
                isLoading={fetching}
                testId='moderator-selection'
              />
              )}
          <Participants>
            {activeParticipantUsers.map(({ name, userId }, index) => (
              <li key={`${userId}-${index}`}>
                <Tag
                  {...(userId === user.id
                    ? {
                        variant: variants.blue
                      }
                    : {
                        variant: variants.light,
                        action: isModerationTypeDisabled ? undefined : () => handleRemoveModerator(userId)
                      }
                  )}
                >
                  {name || userId}
                </Tag>
              </li>
            ))}
          </Participants>
        </AddParticipants>
      )}
    </SettingsContainer>
  )
}

export default ModerationSettings
